/**
 * @deprecated Tenant store — now a READ-ONLY FACADE over useWorkspaceStore.
 *
 * This module used to be a second persisted store for the active client. That
 * made one client two independently mutable values (currentWorkspace UUID
 * object here, currentTenant domain string there) joined by three one-way
 * sync paths — the root cause of header/API desync, stale capability flags,
 * and defensive remount fixes. The selection now lives exactly once in
 * useWorkspaceStore (`ClientSelection`); everything here is DERIVED:
 *
 *   - `currentTenant`  — the activeTenantId projection of the selection
 *                        (workspace.website, or the unlinked deep-link domain).
 *   - `tenants`        — dead collection; the workspace list is the client
 *                        list. Always []. (Its only reader, the retired
 *                        legacy picker, kept its own local list.)
 *   - `brandBibles` / `getBrandBible` — dead cache with zero writers; readers
 *                        already fall through to the API fetch. Always empty.
 *
 * There are NO setters. Write paths go through useWorkspaceStore's actions
 * (selectWorkspace / selectTenantByWebsite / clearSelection); TypeScript
 * rejects any remaining write call site, which is the point — the facade is
 * the compatibility layer while the ~100 legacy importers migrate, not a
 * permanent second abstraction.
 *
 * No persist, no default tenant: a fresh session reads null until
 * ProtectedRoute reconciles a selection. Anything that used to fall back to
 * the AgileCo test tenant now has an explicit no-client guard instead.
 */

import { useShallow } from 'zustand/react/shallow';
import type { TenantSettings, BrandBible } from '../types/tenant';
import {
  activeTenantIdFromSelection,
  useWorkspaceStore,
  type ClientSelection,
} from './useWorkspaceStore';

export interface TenantStoreFacade {
  /** Derived from the active selection. Null when no client is selected. */
  currentTenant: string | null;
  /** @deprecated Dead collection — always []. Use the workspace list. */
  tenants: TenantSettings[];
  /** @deprecated Dead cache — always {}. Brand bible comes from React Query. */
  brandBibles: Record<string, BrandBible>;
  /** @deprecated Dead cache — always undefined; callers fall back to the API. */
  getBrandBible: (website: string) => BrandBible | undefined;
}

// Stable references so useShallow sees the facade as unchanged unless
// currentTenant actually changed — no re-render storms from fresh objects.
const EMPTY_TENANTS: TenantSettings[] = [];
const EMPTY_BRAND_BIBLES: Record<string, BrandBible> = {};
const getEmptyBrandBible = (_website: string): BrandBible | undefined => undefined;

interface WorkspaceStoreSelectionSlice {
  selection: ClientSelection;
}

function deriveTenantFacade(state: WorkspaceStoreSelectionSlice): TenantStoreFacade {
  return {
    currentTenant: activeTenantIdFromSelection(state.selection),
    tenants: EMPTY_TENANTS,
    brandBibles: EMPTY_BRAND_BIBLES,
    getBrandBible: getEmptyBrandBible,
  };
}

interface UseTenantStore {
  (): TenantStoreFacade;
  <T>(selector: (state: TenantStoreFacade) => T): T;
  /** Imperative read for request-time service code. Read-only by design. */
  getState: () => TenantStoreFacade;
}

export const useTenantStore: UseTenantStore = Object.assign(
  function useTenantStore<T>(selector?: (state: TenantStoreFacade) => T) {
    return useWorkspaceStore(
      useShallow((state) => {
        const facade = deriveTenantFacade(state);
        return selector ? selector(facade) : facade;
      }),
    );
  },
  {
    getState: () => deriveTenantFacade(useWorkspaceStore.getState()),
  },
);
